import { CompanyDetail, CompanyResequenceMedia } from '@lib/company';
import { http, http_v2, http_v3, pintHttp } from '@api/client';
import { paramterize, pickResponse, pickResult } from '@api/utils';
import {
  CompanyFilterParams,
  CompanyResult,
  FilterResponse,
  CompanyMergeResponse,
} from './company-api.types';
import { map } from 'rxjs/operators';
import { BaseResponse } from '@api/shared';
import { ServiceHierarchyResponse } from '@pages/company/form/company.types';

/**
 * Filter companies using dynamic operators.
 * @param option Query options
 */
export function filter({
  filters = [],
  pageSize: page_size = 10,
  page = 1,
  sortBy = [],
}: CompanyFilterParams = {}) {
  const isCompanySort = (key: string) =>
    [
      'deals',
      'projects_count',
      'projects_count_2000',
      'number_of_services',
      'clients_count',
    ].includes(key);
  const sort = sortBy
    .filter(({ id }) => isCompanySort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const sort_company_details = sortBy
    .filter(({ id }) => !isCompanySort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const filtersMap: Record<string, string> = filters.reduce(
    (map, f) =>
      Object.assign(map, {
        [f.id]: Object.entries(f.value)
          .map(([op, v]) => {
            return op === '$bw'
              ? `$gte|||${v[0]};$lte|||${v[1]}`
              : op === '$in' &&
                (f.id === 'vtr_company_type_id' ||
                  f.id === 'vtr_company_subtype_id' ||
                  f.id === 'vtr_company_services_id')
              ? `$in|||${v
                  .split(',')
                  .map(v => v.split('|||')[0])
                  .join(',')}`
              : `${op}|||${(v || '').trim()}`;
          })
          .join(';'),
      }),
    {}
  );

  const params = paramterize({ page_size, page, sort, sort_company_details, ...filtersMap });

  const getArr = <T>(val: T): T => (Array.isArray(val) ? val : ([] as any));
  const sortTypes = (a, b) => (a?.order_id || 0) - (b?.order_id || 0);

  return http_v3.get<FilterResponse>('/company/searches', { params }).pipe(
    pickResponse(),
    // Sort company types/subtypes.
    map(res => ({
      ...res,
      data: {
        ...res.data,
        data: (Array.isArray(res?.data?.data) ? res.data.data : []).map(val => ({
          ...val,
          company_types: getArr(val.company_types).sort(sortTypes),
          company_subtypes: getArr(val.company_subtypes).sort(sortTypes),
          company_services: getArr(val.company_services).sort(sortTypes),
          logo_vtr_id: val.logo_source === 's3' ? val.vtr_id : null,
        })),
      },
    }))
  );
}

export function filter_compact({
  filters = [],
  pageSize: page_size = 10,
  page = 1,
  sortBy = [],
}: CompanyFilterParams = {}) {
  const isCompanySort = (key: string) =>
    ['deals', 'projects_count', 'projects_count_2000'].includes(key);

  const sort = sortBy
    .filter(({ id }) => isCompanySort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const sort_company_details = sortBy
    .filter(({ id }) => !isCompanySort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const filtersMap: Record<string, string> = filters.reduce(
    (map, f) =>
      Object.assign(map, {
        [f.id]: Object.entries(f.value)
          .map(([op, v]) => {
            return op === '$bw'
              ? `$gte|||${v[0]};$lte|||${v[1]}`
              : op === '$in' &&
                (f.id === 'vtr_company_type_id' ||
                  f.id === 'vtr_company_subtype_id' ||
                  f.id === 'vtr_company_services_id')
              ? `$in|||${v
                  .split(',')
                  .map(v => v.split('|||')[0])
                  .join(',')}`
              : `${op}|||${(v || '').trim()}`;
          })
          .join(';'),
      }),
    {}
  );

  const params = paramterize({ page_size, page, sort, sort_company_details, ...filtersMap });

  const getArr = <T>(val: T): T => (Array.isArray(val) ? val : ([] as any));
  const sortTypes = (a, b) => (a?.order_id || 0) - (b?.order_id || 0);

  return http.get<FilterResponse>('/company/searches_compact', { params }).pipe(
    pickResponse(),
    // Sort company types/subtypes.
    map(res => ({
      ...res,
      data: {
        ...res.data,
        data: (Array.isArray(res?.data?.data) ? res.data.data : []).map(val => ({
          ...val,
          company_types: getArr(val.company_types).sort(sortTypes),
          company_subtypes: getArr(val.company_subtypes).sort(sortTypes),
          company_services: getArr(val.company_services).sort(sortTypes),
          logo_vtr_id: val.logo_source === 's3' ? val.vtr_id : null,
        })),
      },
    }))
  );
}

export function filter_add_entity({
  filters = [],
  pageSize: page_size = 10,
  page = 1,
  sortBy = [],
}: CompanyFilterParams = {}) {
  const isCompanySort = (key: string) =>
    ['deals', 'projects_count', 'projects_count_2000'].includes(key);

  const sort = sortBy
    .filter(({ id }) => isCompanySort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const sort_company_details = sortBy
    .filter(({ id }) => !isCompanySort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const filtersMap: Record<string, string> = filters.reduce(
    (map, f) =>
      Object.assign(map, {
        [f.id]: Object.entries(f.value)
          .map(([op, v]) => {
            return op === '$bw'
              ? `$gte|||${v[0]};$lte|||${v[1]}`
              : op === '$in' &&
                (f.id === 'vtr_company_type_id' ||
                  f.id === 'vtr_company_subtype_id' ||
                  f.id === 'vtr_company_services_id')
              ? `$in|||${v
                  .split(',')
                  .map(v => v.split('|||')[0])
                  .join(',')}`
              : `${op}|||${(v || '').trim()}`;
          })
          .join(';'),
      }),
    {}
  );

  const params = paramterize({ page_size, page, sort, sort_company_details, ...filtersMap });

  const getArr = <T>(val: T): T => (Array.isArray(val) ? val : ([] as any));
  const sortTypes = (a, b) => (a?.order_id || 0) - (b?.order_id || 0);

  return http.get<FilterResponse>('/company/searches_add_entity', { params }).pipe(
    pickResponse(),
    // Sort company types/subtypes.
    map(res => ({
      ...res,
      data: {
        ...res.data,
        data: (Array.isArray(res?.data?.data) ? res.data.data : []).map(val => ({
          ...val,
          company_types: getArr(val.company_types).sort(sortTypes),
          company_subtypes: getArr(val.company_subtypes).sort(sortTypes),
          company_services: getArr(val.company_services).sort(sortTypes),
          logo_vtr_id: val.logo_source === 's3' ? val.vtr_id : null,
        })),
      },
    }))
  );
}

/**
 * Creates the given company.
 * @param company The company to create
 */
export function create(company: CompanyDetail) {
  return http.post<any>(`/company`, company).pipe(pickResponse());
}

/**
 * Get the company details from the server.
 * @param id The id of the company to get
 */
export function get(id: string) {
  return http.get<CompanyResult>(`/company/${id}`).pipe(pickResult());
}

/**
 * Get the company details from the server.
 * @param query_params The query params of the company to get
 */
export function getCompanies(query_params: string) {
  return http.get<CompanyDetail>(`api/v1/company/searches?${query_params}`).pipe(pickResult());
}

/**
 * Updates the company
 * @param company_vtr_id vtr_id for the company getting updated
 * @param company -> company data
 */
export function update(company_vtr_id: string, company: CompanyDetail) {
  return http.put<any>(`/company/${company_vtr_id}`, company).pipe(pickResponse());
}



/**
 * Updates the company
 * @param company_vtr_id vtr_id for the company getting updated
 * @param company -> company data
 */
 export function clearcompanycache(company_vtr_id: string) {

   http.get<any>(`/clear-company-cache/${company_vtr_id}`).pipe(pickResponse());
   return http.get<CompanyResult>(`/company/${company_vtr_id}`).pipe(pickResult());
}
/**
 * Merge the given list of companies.
 * @param company The merged company.
 * @param toMerge The list of ids to merge.
 */
export function merge(company: CompanyDetail, toMerge: string[]) {
  delete company.imdb_company_id;
  delete company.imdb_company_name;
  delete company.imdb_parent_company_id;
  delete company.imdb_parent_company_name;
  delete company.linked_imdb_company_id;
  delete company.linked_imdb_company_name;
  delete company.social_imdb_pro_url;
  return http
    .post<BaseResponse<CompanyMergeResponse>>('/company/dedupe', {
      new_company_data: company,
      company_ids: toMerge,
    })
    .pipe(pickResult());
}

/**Refresh APi for company updation on PINT */
export function refreshCompanyOnPINT(company_vtr_id: string) {
  return pintHttp.get(`/api/v1/cmd/company/${company_vtr_id}/refresh`);
}

/**
 * Filter companies using dynamic operators. Used only for company merge
 * @param option Query options
 */
export function filterV2({
  filters = [],
  pageSize: page_size = 10,
  page = 1,
  sortBy = [],
}: CompanyFilterParams = {}) {
  const isCompanySort = (key: string) =>
    [
      'deals',
      'projects_count',
      'projects_count_2000',
      'number_of_services',
      'clients_count',
    ].includes(key);

  const sort = sortBy
    .filter(({ id }) => isCompanySort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const sort_company_details = sortBy
    .filter(({ id }) => !isCompanySort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const filtersMap: Record<string, string> = filters.reduce(
    (map, f) =>
      Object.assign(map, {
        [f.id]: Object.entries(f.value)
          .map(([op, v]) => {
            return op === '$bw'
              ? `$gte|||${v[0]};$lte|||${v[1]}`
              : op === '$in' &&
                (f.id === 'vtr_company_type_id' ||
                  f.id === 'vtr_company_subtype_id' ||
                  f.id === 'vtr_company_services_id')
              ? `$in|||${v
                  .split(',')
                  .map(v => v.split('|||')[0])
                  .join(',')}`
              : `${op}|||${(v || '').trim()}`;
          })
          .join(';'),
      }),
    {}
  );

  const params = paramterize({ page_size, page, sort, sort_company_details, ...filtersMap });

  const getArr = <T>(val: T): T => (Array.isArray(val) ? val : ([] as any));
  const sortTypes = (a, b) => (a?.order_id || 0) - (b?.order_id || 0);

  return http_v2.get<FilterResponse>('/company/searches', { params }).pipe(
    pickResponse(),
    // Sort company types/subtypes.
    map(res => ({
      ...res,
      data: {
        ...res.data,
        data: (Array.isArray(res?.data?.data) ? res.data.data : []).map(val => ({
          ...val,
          company_types: getArr(val.company_types).sort(sortTypes),
          company_subtypes: getArr(val.company_subtypes).sort(sortTypes),
          company_services: getArr(val.company_services).sort(sortTypes),
          logo_vtr_id: val.logo_source === 's3' ? val.vtr_id : null,
        })),
      },
    }))
  );
}

 /** resequence media payload  */
 
 export function resequence(media: CompanyResequenceMedia, id: string) {
  return http.post<any>(`/company/media_resequence/${id}`, media).pipe(pickResponse());
}

export function getServiceHierarchy(serviceIds: string) {
  return http
    .get<ServiceHierarchyResponse>(`/company/service-hierarchy`, {
      params: { service_vtr_ids: serviceIds },
    })
    .pipe(pickResponse());
}